import React, { useEffect, useState } from 'react';
import { store } from '../../../app/store';
import { postAPI } from '../../../app/api';
import { ToastContainer, toast, Slide } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import FormSubmissionHandler from '../../../UIComponents/FormSubmissionHandler';
import FileUploadHandler from '../../../UIComponents/FileUploadHandler';
import NormalTextareaHandler from '../../../UIComponents/NormalTextareaHandler';
import InputHandler from '../../../UIComponents/InputHandler';
import Loader from '../../../UIComponents/Loader';
import { hasPermission, isAdmin } from '../../../store/hooks/Permission';

export default function Form({ is_edit, edit_id }) {
  const [isBTNClicked, setIsBTNClicked] = useState(false);
  const store_data = JSON.parse(store());
  const [canCreate, setCanCreate] = useState(false);
  const [canEdit, setCanEdit] = useState(false);

  const initialData = {
    book_name: '',
    author_name: '',
    publisher_name: '',
    price: '',
    purchase_link: '',
    details: '',
    book_image: '',
  };

  const [formData, setFormData] = useState(initialData);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchSingleData = async (id) => {
    const response = await postAPI('/books/fetchSingle', { org_id: store_data?.org_id, id, user: store_data });
    const bookData = response?.data?.length > 0 ? response?.data[0] : null;

    if (response?.status === 1 && bookData) {
      setFormData({
        id: bookData.id,
        book_name: bookData.book_name || '',
        author_name: bookData.author_name || '',
        publisher_name: bookData.publisher_name || '',
        price: bookData.price ?? '',
        purchase_link: bookData.purchase_link || '',
        details: bookData.details || '',
        book_image: bookData.book_image || '',
      });
    }
  };

  useEffect(() => {
    const checkPermissions = async () => {
      if (isAdmin()) {
        setCanCreate(true);
        setCanEdit(true);
      } else {
        const createAllowed = await hasPermission('BOOKS', 'Books', 'CREATE');
        const editAllowed = await hasPermission('BOOKS', 'Books', 'EDIT');
        setCanCreate(createAllowed);
        setCanEdit(editAllowed);
      }
    };

    const init = async () => {
      await checkPermissions();
      if (is_edit) {
        await fetchSingleData(edit_id);
      }
      setLoading(false);
    };

    init();
  }, [is_edit, edit_id]);

  const validate = () => {
    const localErrors = [];

    if (!formData.book_name?.trim()) localErrors.push('Book Name is required.');
    if (!formData.author_name?.trim()) localErrors.push('Author Name is required.');
    if (!formData.publisher_name?.trim()) localErrors.push('Publisher Name is required.');
    if (formData.price === '' || formData.price === null || formData.price === undefined) {
      localErrors.push('Price is required.');
    } else if (Number.isNaN(Number(formData.price))) {
      localErrors.push('Price must be a valid number.');
    }

    setErrors(localErrors);
    return localErrors.length === 0;
  };

  const handleSubmit = async () => {
    if (isBTNClicked) return;

    if (!isAdmin() && ((is_edit && !canEdit) || (!is_edit && !canCreate))) {
      toast.error('You do not have permission to perform this action.');
      return;
    }

    if (!validate()) {
      toast.error('Please fix validation errors.');
      return;
    }

    setIsBTNClicked(true);

    const payload = {
      org_id: store_data?.org_id,
      ...formData,
      user: store_data,
    };

    const response = await postAPI(is_edit ? '/books/update' : '/books/create', payload);

    if (response?.status === 1) {
      toast.success(is_edit ? 'Book updated successfully.' : 'Book created successfully.');
      window.location.reload();
    } else {
      toast.error(response?.error || 'Sorry! Something went wrong.');
      setIsBTNClicked(false);
    }
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (!isAdmin() && !canCreate && !canEdit) ? (
        <div className="alert alert-danger">You do not have permission to access this form.</div>
      ) : (
        <FormSubmissionHandler handleSubmit={handleSubmit} editing={is_edit} error={errors} BTNLabel="Save" IsBTNClicked={isBTNClicked}>
          <div className="col-12 col-md-6 mt-0">
            <InputHandler form_data={formData} setFormData={setFormData} Name="book_name" Type="text" Label="Book Name" />
          </div>
          <div className="col-12 col-md-6 mt-0">
            <InputHandler form_data={formData} setFormData={setFormData} Name="author_name" Type="text" Label="Author Name" />
          </div>
          <div className="col-12 col-md-6 mt-0">
            <InputHandler form_data={formData} setFormData={setFormData} Name="publisher_name" Type="text" Label="Publisher Name" />
          </div>
          <div className="col-12 col-md-6 mt-0">
            <InputHandler form_data={formData} setFormData={setFormData} Name="price" Type="number" Label="Price" />
          </div>
          <div className="col-12 col-md-12 mt-0">
            <InputHandler form_data={formData} setFormData={setFormData} Name="purchase_link" Type="text" Label="Purchase Link" />
          </div>
          <div className="col-12 col-md-12 mt-0">
            <NormalTextareaHandler form_data={formData} setFormData={setFormData} Name="details" Label="Details" />
          </div>
          <div className="col-12 col-md-12 mt-0">
            <FileUploadHandler
              Name="book_image"
              form_data={formData}
              setFormData={setFormData}
              error={errors}
              setError={setErrors}
              Section="Books"
              Label="Upload Book Image"
            />
          </div>

          {errors.length > 0 && (
            <div className="alert alert-danger mt-2">
              <ul>{errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
            </div>
          )}
        </FormSubmissionHandler>
      )}

      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        hideProgressBar={true}
        closeOnClick={true}
        pauseOnHover={false}
        draggable={false}
        progress="1"
        theme="dark"
        transition={Slide}
      />
    </>
  );
}
